import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth';
import { buildsApi } from '../../api/builds';
import type { Build } from '../../api/builds';
import { Layout } from '../../components/Layout';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import {
  Upload,
  CheckCircle,
  Clock,
  XCircle,
  Coins,
  Activity,
  ArrowRight,
  Zap,
  TrendingUp,
  Shield,
} from 'lucide-react';

export function DashboardPage() {
  const { user } = useAuthStore();
  const [recentBuilds, setRecentBuilds] = useState<Build[]>([]);
  const [stats, setStats] = useState({ total: 0, completed: 0, failed: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const response = await buildsApi.list(1, 5);
      setRecentBuilds(response.data || []);
      
      const completed = (response.data || []).filter((b) => b.status === 'COMPLETED').length;
      const failed = (response.data || []).filter((b) => b.status === 'FAILED').length;
      setStats({
        total: response.pagination?.total || 0,
        completed,
        failed,
      });
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const configs: Record<string, { icon: React.ReactNode; class: string; label: string }> = {
      COMPLETED: {
        icon: <CheckCircle className="w-3.5 h-3.5" />,
        class: 'badge-completed',
        label: 'Completed',
      },
      PROCESSING: {
        icon: <Clock className="w-3.5 h-3.5 animate-spin" />,
        class: 'badge-processing',
        label: 'Processing',
      },
      PENDING: {
        icon: <Clock className="w-3.5 h-3.5" />,
        class: 'badge-pending',
        label: 'Pending',
      },
      FAILED: {
        icon: <XCircle className="w-3.5 h-3.5" />,
        class: 'badge-failed',
        label: 'Failed',
      },
    };
    
    const config = configs[status] || configs.PENDING;
    return (
      <span className={`badge ${config.class}`}>
        {config.icon}
        {config.label}
      </span>
    );
  };

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header */}
        <div className="opacity-0-init animate-fade-in-up">
          <div className="flex items-center gap-2 text-sm text-slate-500 mb-2 font-mono">
            <Activity className="w-4 h-4 text-cyan-500" />
            <span>Dashboard</span>
          </div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2">
            Welcome back, <span className="text-gradient">{user?.username}</span>
          </h1>
          <p className="text-slate-400 text-sm sm:text-base lg:text-lg">
            Manage your protected builds and configurations
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Credits Card */}
          <Card 
            variant="glow" 
            hover 
            className="opacity-0-init animate-fade-in-up animation-delay-100"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-400 text-xs sm:text-sm mb-1">Available Credits</p>
                <p className="text-3xl sm:text-4xl font-bold text-gradient">{user?.credits || 0}</p>
                <p className="text-slate-500 text-xs mt-2 font-mono">1 credit = 1 build</p>
              </div>
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-gradient-to-br from-cyan-500/20 to-purple-500/20 flex items-center justify-center border border-cyan-500/20">
                <Coins className="w-6 h-6 sm:w-7 sm:h-7 text-cyan-400" />
              </div>
            </div>
          </Card>

          {/* Success Rate Card */}
          <Card 
            hover 
            className="opacity-0-init animate-fade-in-up animation-delay-200"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-400 text-xs sm:text-sm mb-1">Successful Builds</p>
                <p className="text-3xl sm:text-4xl font-bold text-white">{stats.completed}</p>
                <div className="flex items-center gap-1.5 mt-2">
                  <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="text-emerald-400 text-xs font-medium">
                    {stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0}% success rate
                  </span>
                </div>
              </div>
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20">
                <Shield className="w-6 h-6 sm:w-7 sm:h-7 text-emerald-400" />
              </div>
            </div>
          </Card>

          {/* Total Builds Card */}
          <Card 
            hover 
            className="opacity-0-init animate-fade-in-up animation-delay-300 sm:col-span-2 lg:col-span-1"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-400 text-xs sm:text-sm mb-1">Total Builds</p>
                <p className="text-3xl sm:text-4xl font-bold text-white">{stats.total}</p>
                <p className="text-slate-500 text-xs mt-2 font-mono">All time</p>
              </div>
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-purple-500/10 flex items-center justify-center border border-purple-500/20">
                <Activity className="w-6 h-6 sm:w-7 sm:h-7 text-purple-400" />
              </div>
            </div>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card 
          title="Quick Actions" 
          className="opacity-0-init animate-fade-in-up animation-delay-400"
        >
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
            <Link to="/builds/new" className="flex-1 sm:flex-none">
              <Button glow className="w-full sm:w-auto">
                <Zap className="w-4 h-4" />
                New Build
              </Button>
            </Link>
            <Link to="/builds" className="flex-1 sm:flex-none">
              <Button variant="secondary" className="w-full sm:w-auto">
                <Activity className="w-4 h-4" />
                View All Builds
              </Button>
            </Link>
            <Link to="/credits" className="flex-1 sm:flex-none">
              <Button variant="ghost" className="w-full sm:w-auto">
                <Coins className="w-4 h-4" />
                Get Credits
              </Button>
            </Link>
          </div>
        </Card>

        {/* Recent Builds */}
        <Card 
          title="Recent Builds" 
          subtitle="Your latest protection tasks"
          className="opacity-0-init animate-fade-in-up animation-delay-500"
        >
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="relative">
                <div className="w-12 h-12 rounded-full border-2 border-slate-700" />
                <div className="absolute inset-0 w-12 h-12 rounded-full border-2 border-cyan-500 border-t-transparent animate-spin" />
              </div>
            </div>
          ) : recentBuilds.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-slate-800/50 flex items-center justify-center border border-slate-700/50">
                <Upload className="w-10 h-10 text-slate-600" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">No builds yet</h3>
              <p className="text-slate-500 mb-6">Start protecting your software now</p>
              <Link to="/builds/new">
                <Button glow>
                  <Zap className="w-4 h-4" />
                  Create Your First Build
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {recentBuilds.map((build, index) => (
                <Link
                  key={build.id}
                  to={`/builds/${build.id}`}
                  className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 rounded-xl bg-slate-800/30 hover:bg-slate-800/50 border border-slate-700/30 hover:border-cyan-500/20 transition-all duration-300 group opacity-0-init animate-fade-in-up gap-3"
                  style={{ animationDelay: `${0.6 + index * 0.1}s` }}
                >
                  <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center border border-slate-700/50 flex-shrink-0">
                      <Upload className="w-5 h-5 text-slate-400" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-white group-hover:text-cyan-400 transition-colors truncate text-sm sm:text-base">
                        {build.filename}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-slate-500 font-mono">
                          {new Date(build.createdAt).toLocaleDateString()}
                        </span>
                        <span className="text-slate-700">•</span>
                        <span className="text-xs text-purple-400 font-mono uppercase">
                          {build.engineType}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-4 pl-13 sm:pl-0">
                    {getStatusBadge(build.status)}
                    <ArrowRight className="w-4 h-4 text-slate-600 group-hover:text-cyan-400 group-hover:translate-x-1 transition-all" />
                  </div>
                </Link>
              ))}
              
              {recentBuilds.length > 0 && (
                <div className="pt-4 text-center">
                  <Link
                    to="/builds"
                    className="inline-flex items-center gap-2 text-sm text-cyan-400 hover:text-cyan-300 transition-colors font-medium"
                  >
                    View all builds
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
}
